import { useLayoutEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const FROM = {
  up: { y: 70, opacity: 0 },
  left: { x: -90, opacity: 0 },
  right: { x: 90, opacity: 0 },
  zoom: { scale: 0.88, opacity: 0 },
};

/**
 * Data attributes (put them on any element inside `root`):
 *  data-reveal="up|left|right|zoom"  one-shot reveal
 *  data-stagger                      children cascade in
 *  data-parallax="0.1"               scrubbed vertical parallax (relative to parent)
 *  data-drift="-20"                  scrubbed horizontal drift in % (big words)
 *  data-clip-up                      one-shot curtain reveal from the bottom
 *  .fx-clip                          scrubbed inset-frame image reveal
 *  .fx-line > span                   headline line mask (used by page intros)
 */
export default function useScrollFx(root, extra) {
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        gsap.to(".scroll-progress", {
          scaleX: 1,
          ease: "none",
          scrollTrigger: { start: 0, end: "max", scrub: 0.3 },
        });

        gsap.utils.toArray("[data-reveal]").forEach((el) => {
          gsap.from(el, {
            ...FROM[el.dataset.reveal || "up"],
            duration: 1.1,
            ease: "power3.out",
            clearProps: "transform,opacity",
            scrollTrigger: { trigger: el, start: "top 88%", once: true },
          });
        });

        gsap.utils.toArray("[data-stagger]").forEach((group) => {
          gsap.from(group.children, {
            y: 90,
            opacity: 0,
            rotateX: -18,
            transformOrigin: "50% 100%",
            duration: 1,
            ease: "power3.out",
            stagger: 0.12,
            clearProps: "transform,opacity",
            scrollTrigger: { trigger: group, start: "top 88%", once: true },
          });
        });

        gsap.utils.toArray("[data-clip-up]").forEach((el) => {
          gsap.fromTo(
            el,
            { clipPath: "inset(100% 0% 0% 0%)" },
            {
              clipPath: "inset(0% 0% 0% 0%)",
              duration: 1.2,
              ease: "power4.inOut",
              scrollTrigger: { trigger: el, start: "top 90%", once: true },
            },
          );
        });

        gsap.utils.toArray(".fx-clip").forEach((el) => {
          gsap.fromTo(
            el,
            { clipPath: "inset(12% 12% 12% 12% round 24px)", scale: 0.92 },
            {
              clipPath: "inset(0% 0% 0% 0% round 14px)",
              scale: 1,
              ease: "none",
              scrollTrigger: {
                trigger: el,
                start: "top 90%",
                end: "top 35%",
                scrub: true,
              },
            },
          );
        });

        gsap.utils.toArray("[data-parallax]").forEach((el) => {
          const s = parseFloat(el.dataset.parallax);
          gsap.fromTo(
            el,
            { yPercent: -s * 100 },
            {
              yPercent: s * 100,
              ease: "none",
              scrollTrigger: {
                trigger: el.parentElement,
                start: "top bottom",
                end: "bottom top",
                scrub: true,
              },
            },
          );
        });

        gsap.utils.toArray("[data-drift]").forEach((el) => {
          gsap.fromTo(
            el,
            { xPercent: 0 },
            {
              xPercent: parseFloat(el.dataset.drift),
              ease: "none",
              scrollTrigger: {
                trigger: el,
                start: "top bottom",
                end: "bottom top",
                scrub: true,
              },
            },
          );
        });

        gsap.utils.toArray(".section-title").forEach((t) => {
          gsap.from(t.children, {
            y: 40,
            opacity: 0,
            duration: 0.9,
            stagger: 0.1,
            ease: "power3.out",
            clearProps: "transform,opacity",
            scrollTrigger: { trigger: t, start: "top 90%", once: true },
          });
        });

        if (extra) extra(gsap, ScrollTrigger);
      });

      const refresh = () => ScrollTrigger.refresh();
      window.addEventListener("load", refresh);
      return () => window.removeEventListener("load", refresh);
    }, root);

    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
